import * as React from 'react'
import { useIO, useLinked } from "@type-r/react";
import { attributes, AttributesMixin, define, Events, Model, type } from "@type-r/models";
import { globalStore } from "api";
import { fetchJson } from "api/endpoint";
import { asDate, CollapsableSection, Flex, Link, Loading, TilesList } from "lqx-react/lib";
import { SmallBox } from "src/app/pages/inmatch/invoices/invoicesList";
import { ListField } from "src/app/pages/inmatch/matching/unreconciled/invoicesList";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { TimeRangeSelector } from '../../common';
import { TimeRangeTypes } from '../../api';

const TIME_RANGE_DEFAULT = TimeRangeTypes.slice(1)[0].val;
const InblockUploadResult = attributes({
    id: Number,
    fileName: String,
    filePath: String,
    fileType: String
})

@define
export class LatestRelationshipStatementSent extends Model {

    static attributes = {
        relationshipId: Number,
        customerName: String,
        customerExternalId: String,
        sentDate: type(Date).null,
        file: InblockUploadResult
    };

    static async fetchSoaSent(timeRangeValue) {
        return await fetchJson('POST', `/api/v2/invoice-management/landing/review-items/soa-sent/${timeRangeValue}`);
    }
}

export interface LatestRelationshipStatementSent extends AttributesMixin<typeof LatestRelationshipStatementSent> { }

@define
export class ReviewSentOutSoaState extends attributes({
    invoices: [LatestRelationshipStatementSent],
    isReady: Boolean
}) {
    async fetch(options?) {

        var resp = await LatestRelationshipStatementSent.fetchSoaSent(options ? options.$timeRange.value : TIME_RANGE_DEFAULT);

        this.invoices.set(resp);
        this.invoices.updateEach(element => {
            let customer = globalStore.getRelationshipById(element.relationshipId).Customer;
            element.customerName = customer.companyName;
            element.customerExternalId = customer.externalRelationshipId;
        });

        return this;
    }

    getCount() {
        return this.invoices ? this.invoices.length : 0;
    }
}

export const SentOutSOA = ({ state }: { state: ReviewSentOutSoaState }) => {

    const $timeRange = useLinked(TIME_RANGE_DEFAULT);

    state.isReady = useIO(async () =>

        await state.fetch({ $timeRange }

        ), [$timeRange['_changeToken']]);

    return (
        <CollapsableSection title={`Review Items - ${state.getCount()}`}>
            {state.isReady
                ? <>
                    <Flex row>
                        <Flex grow />
                        {$timeRange ? <TimeRangeSelector $timeRange={$timeRange} timeRangeTypes={TimeRangeTypes.slice(1)} /> : <></>}
                    </Flex>
                    <TilesList
                        items={Array.from(state.invoices)}
                        itemSize={60}
                        renderer={OpenItem()}
                        selectedItem={null}
                        height={600} />
                </>
                : <Loading />}
        </CollapsableSection>
    );
}

const OpenItem = () => ({ data, style, index }) => {
    const item = data.items[index] as LatestRelationshipStatementSent;
    const styles = openItemStyles();

    return (
        <div style={style}>
            <SmallBox>
                <div className={styles.customerDetails} title={item.customerName}>
                    <Flex column>
                        <ListField className={styles.listField}>{item.customerName}</ListField>
                        <ListField className={styles.listField}>{item.customerExternalId}</ListField>
                    </Flex>
                </div>
                <ListField className={styles.listFieldL}>
                    SOA sent: {asDate(item.sentDate)}
                </ListField>

                <ListField className={styles.listFieldLink} >
                    <Link onClick={() => Events.trigger('navigate', `/invoices/statement?relationshipId=${item.relationshipId}`)}>View: SOA</Link>
                </ListField>
                <Flex grow></Flex>
            </SmallBox>
        </div>
    )
}

export const DownloadLoading = () => {
    return (
        <Flex center>
            <CircularProgress style={{ height: 22, width: 22 }} />
        </Flex>
    )
}

const openItemStyles = makeStyles({
    customerDetails: {
        width: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },
    amountSum: {
        width: 200,
        color: "black"
    },
    listField: {
        color: "black"
    },
    listFieldL: {
        width: 200,
        color: "black"
    },
    listFieldLink: {
        color: "#0056b3",
        "&:hover": {
            cursor: "pointer",
            textDecoration: "underline"
        }
    }
});

